import React, { useEffect, useMemo, useState } from 'react';
import { logHours, readServerError } from '../../api/deliveryApi';
import { resolveNbAnchor } from '../../api/timesheetApi';
import { useToast } from '../Common/ToastProvider';
import './NonBillableDrawer.css';

// MT-S2 — non-billable log drawer. Two modes:
//   (a) editing an existing NB row  → initial projectId + note pre-filled;
//   (b) "+ Add non-billable task"   → user picks project + writes note.
// Both end in POST /work-items/{sysWi}/hours with {date, hours, note}.
// The drawer NEVER creates the system WI itself — it calls
// /api/timesheet/nb-anchor and takes the id back. That endpoint is
// idempotent, so switching projects during a session is cheap.
//
// Locked month → everything read-only; the header shows a 🔒.

const PRESETS = [2, 4, 6, 8];
const clampQuarter = (n) => Math.max(0, Math.round((Number(n) || 0) / 0.25) * 0.25);
const fmt = (n) => (Number(n) || 0).toString().replace('.', ',');

export default function NonBillableDrawer({
  open, onClose,
  date,               // 'YYYY-MM-DD' — the day being logged
  projects,           // [{ id, name, isBillable:false }] the caller's NB projects
  initialProjectId,
  initialHours = 0,
  initialNote = '',
  locked = false,
  onSaved,            // (payload) => void — parent refreshes my-days/my-calendar
}) {
  const toast = useToast();
  const [projectId, setProjectId] = useState(initialProjectId ?? (projects[0]?.id ?? 0));
  const [hours, setHours] = useState(initialHours);
  const [note, setNote] = useState(initialNote);
  const [saving, setSaving] = useState(false);
  // The resolved system WI for the CURRENT (projectId, caller). Reset
  // whenever the project changes; anchoring is lazy — nothing hits the
  // wire until the user clicks Log/Update.
  const [anchorWi, setAnchorWi] = useState(null);
  const [anchorErr, setAnchorErr] = useState('');

  useEffect(() => {
    setAnchorWi(null);
    setAnchorErr('');
    if (!projectId || !open) return undefined;
    let cancelled = false;
    resolveNbAnchor(projectId)
      .then((r) => { if (!cancelled) setAnchorWi(r?.workItemId ?? null); })
      .catch((e) => { if (!cancelled) setAnchorErr(readServerError(e)); });
    return () => { cancelled = true; };
  }, [projectId, open]);

  const projectName = useMemo(
    () => (projects.find((p) => p.id === projectId)?.name) || '—',
    [projects, projectId],
  );

  if (!open) return null;

  const step = (delta) => setHours((v) => clampQuarter((Number(v) || 0) + delta));
  const preset = (v) => setHours(clampQuarter(v));

  const doSave = async () => {
    if (locked) return;
    if (!anchorWi) return;
    setSaving(true);
    try {
      await logHours(anchorWi, [{ date, hours: Number(hours) || 0, note: (note ?? '') }]);
      if (typeof onSaved === 'function') onSaved({ projectId, workItemId: anchorWi, date });
      onClose?.();
    } catch (e) {
      toast?.(readServerError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nb-root" data-testid="nb-drawer">
      <div className="nb-ov" onClick={onClose} />
      <div className="nb-dr">
        <div className="nb-hd">
          <span className="nb-x" onClick={onClose}>✕</span>
          <h3>Non-billable — {projectName}</h3>
        </div>
        <div className="nb-bd">
          <div className="nb-sec">Context</div>
          <div className="nb-kv">
            <span className="nb-k">Project</span>
            <span>
              <select
                data-testid="nb-project"
                disabled={locked || saving}
                value={projectId || ''}
                onChange={(e) => setProjectId(Number(e.target.value))}
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {/* MT-S3.2 — anchor-resolution failure surfaces inline
                  under the Project select (not via toast) so the
                  consultant sees why Log is disabled without moving
                  their eyes. */}
              {anchorErr && (
                <div className="nb-error" data-testid="nb-error">{anchorErr}</div>
              )}
            </span>
          </div>
          <div className="nb-sec">Work description</div>
          <textarea
            data-testid="nb-note"
            disabled={locked || saving}
            placeholder="Describe the work…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="nb-sec">Hours — this day</div>
          <div className="nb-daycard">
            <div className="nb-nm">{date}{locked ? ' · 🔒 month locked' : ''}</div>
            <div className="nb-vl">{hours ? `${fmt(hours)} h` : 'no entry'}</div>
            {!locked && (
              <>
                <div className="nb-stepper">
                  <button type="button" onClick={() => step(-1)}>−</button>
                  <input
                    data-testid="nb-hours"
                    value={hours || ''}
                    onChange={(e) => setHours(clampQuarter(parseFloat(String(e.target.value).replace(',', '.'))))}
                  />
                  <button type="button" onClick={() => step(1)}>＋</button>
                </div>
                <div className="nb-presets">
                  {PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => preset(p)}>{p} h</button>
                  ))}
                </div>
                <button
                  type="button"
                  className="nb-btn nb-primary"
                  data-testid="nb-log"
                  disabled={saving || !anchorWi}
                  onClick={doSave}
                >{initialHours > 0 ? 'Update' : 'Log'}</button>
              </>
            )}
          </div>
        </div>
        <div className="nb-ftr">
          <button type="button" className="nb-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
